import React, { useRef } from 'react';
import { Link } from '@shopify/hydrogen';
import {
  A11y,
  Autoplay,
  EffectFade,
  Keyboard,
  Navigation,
  Pagination,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { v4 as uuid } from 'uuid';

export function Hero(data) {
  const slide = data?.data?.edges?.map((slide) => slide.node);
  const fields = slide.map((slide) => slide?.fields);

  const orderByPrimaryFirst = fields.sort((a, b) => {
    const aPrimary = a?.find((f) => f?.key === 'primary')?.value === 'true';
    const bPrimary = b?.find((f) => f?.key === 'primary')?.value === 'true';
    return aPrimary === bPrimary ? 0 : aPrimary ? -1 : 1;
  });

  const heroRef = useRef();

  const swiperParams = {
    modules: [Pagination, EffectFade, Autoplay, A11y, Keyboard, Navigation],
    effect: 'fade',
    fadeEffect: { crossFade: true },
    speed: 2000,
    pagination: {
      type: 'fraction',
      renderFraction(currentClass, totalClass) {
        return (
          `<span class="${currentClass}"></span>` +
          ' | ' +
          `<span class="${totalClass}"></span>`
        );
      },
    },
    autoplay: { enabled: true, delay: 8000, disableOnInteraction: false, pauseOnMouseEnter: true },
    loop: true,
    keyboard: { enabled: true },
    watchSlidesProgress: true,
    observer: true,
    observeParents: true,
    threshold: 5,
    slidesPerGroupAuto: false,
  };

  return (
    <div className="hero-swiper">
      <Swiper
        {...swiperParams}
        className="relative h-full w-full"
        onBeforeInit={(swiper) => {
          heroRef.current = swiper;
        }}
      >
        {orderByPrimaryFirst?.map((field) => {
          const callToAction = field?.find((f) => f?.key === 'call_to_action')?.value;
          const title = field?.find((f) => f?.key === 'title')?.value;

          const desktopImage = field
            ?.find((f) => f?.key === 'desktop_image')?.reference?.image?.url || '';
          const mobileImage = field
            ?.find((f) => f?.key === 'mobile_image')?.reference?.image?.url || '';

          const url = field
            ?.find((f) => f?.reference?.onlineStoreUrl)
            ?.reference?.onlineStoreUrl?.replace(import.meta.env.PUBLIC_ONLINE_STORE_URL, '') || '';

          const SlideContent = (
            <div className="h-full w-full" key={uuid()}>
              <div className="hero-slide-gradient" />
              <div
                className="absolute top-0 left-0 hidden md:flex"
                style={{
                  backgroundImage: `url(${desktopImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  width: '100%',
                  height: '100%',
                }}
              />
              <div
                className="absolute top-0 left-0 flex md:hidden"
                style={{
                  backgroundImage: `url(${mobileImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'top',
                  backgroundRepeat: 'no-repeat',
                  width: '100%',
                  height: '100%',
                }}
              />
              <div className="swiper-box absolute bottom-0 md:bottom-[2.5rem] flex justify-center mx-auto left-0 right-0 items-center w-full md:w-[520px] h-[150px] md:h-[125px] bg-white">
                <div className="flex flex-col justify-center items-center space-y-2 swiper-text">
                  <h4 className="max-w-md swiper-title">{title}</h4>
                  <h4 className="swiper-cta">{callToAction}</h4>
                </div>
              </div>
            </div>
          );

          return (
            <SwiperSlide key={uuid()}>
              {url ? <Link to={url}>{SlideContent}</Link> : SlideContent}
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className="swiper-button-navigation">
        <button
          className="hero-button-prev swiper-button-prev left-caret"
          onClick={() => heroRef.current?.slidePrev()}
        >
          <LeftCaret />
        </button>
        <div className="hero-swiper-pagination" />
        <button
          className="hero-button-next swiper-button-next right-caret"
          onClick={() => heroRef.current?.slideNext()}
        >
          <RightCaret />
        </button>
      </div>
    </div>
  );
}

export function LeftCaret() {
  return (
    <svg width="7" height="13" viewBox="0 0 7 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 6.5L7 0.5V12.5L0 6.5Z" fill="black" />
    </svg>
  );
}

export function RightCaret() {
  return (
    <svg width="7" height="13" viewBox="0 0 7 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 6.5L0 0.5V12.5L7 6.5Z" fill="black" />
    </svg>
  );
}
